// Common types needed across individual unit groups and the overall combined
// units.

import { UnitOptions } from '../schema/utils';

export const UNIT_GROUPS = ['Time', 'Percent', 'Decimal', 'Bytes', 'Custom'] as const;
export type UnitGroup = (typeof UNIT_GROUPS)[number];

/**
 * Configuration for rendering units that are part of a group.
 */
export type UnitGroupConfig = {
  /**
   * The label that is shown in the UI.
   */
  label: string;
  /**
   * When true, the unit group supports setting decimal places.
   */
  decimal_places?: boolean;
  /**
   * When true, the unit group supports enabling abbreviate.
   */
  abbreviate?: boolean;
};

/**
 * Configuration for rendering a specific unit.
 */
export type UnitConfig = {
  /**
   * The group the unit is part of. This will inform common rendering behavior.
   */
  group: UnitGroup;

  /**
   * When true, this unit will not be displayed in the unit selector. This is
   * useful for units that are shorthand variants of other units.
   */
  disableSelectorOption?: boolean;

  /**
   * The label that is shown in the UI.
   */
  label: string;

  /**
   * The description that is shown in the UI.
   */
  description?: string;
};

/**
 * Type for tests for formatValue function.
 */
export interface FormatValueTestCase {
  value: number;
  options: FormatValueOptions;
  expected: string;
}

/**
 * Options to the formatValue function.
 */
export type FormatValueOptions = UnitOptions & {
  /**
   * Whether to trim trailing zeros.
   * For example:
   * 1.2 and { unit: 'Decimal', decimal_places: 2, trim_zeros: false } will format to 1.20.
   * 1.2 and { unit: 'Decimal', decimal_places: 2, trim_zeros: true } will format to 1.2.
   */
  trimZeros?: boolean;
  /**
   * When true, values below the tiny-value threshold (0.0001) are formatted using scientific notation.
   * Currently restricted to `Bytes` units.
   * TODO: Consider whether this should be used for other units.
   */
  scientificForTinyValues?: boolean;
};
